import { PLAYER_ID } from './protocol';

/**
 * The beta's two channels back from players, shared by the game and the server (worker/beta.ts):
 *   feedback  a few words a player types ("tell us"), read on the stats page
 *   crashes   what went wrong on a player's device (an error, the 3D failing, a crash), grouped on the stats page
 * Neither carries anything that identifies a person: feedback is filed under the same scrambled id the stats
 * use (it changes every day), and both carry only the kind of device and browser.
 */

export const FEEDBACK_MAX = 600;

export interface FeedbackBody {
  playerId: string;
  text: string;
  /** "iPhone · Safari 18" */
  browser: string;
  device: 'phone' | 'computer';
  /** Where it was written from: the poster, the pause menu. */
  place: 'poster' | 'paused';
}

export type CrashKind = 'crash' | 'error' | 'no3d' | 'gl-lost';
const CRASH_KINDS: CrashKind[] = ['crash', 'error', 'no3d', 'gl-lost'];

export interface CrashBody {
  kind: CrashKind;
  message: string;
  stack: string;
  browser: string;
  device: 'phone' | 'computer';
  /** What the game was doing: on the poster, racing, watching a lap. */
  mode: string;
  /** Which build of the game. */
  build: string;
}

const short = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max;

export function checkFeedback(b: unknown): string | null {
  if (!b || typeof b !== 'object') return 'bad request';
  const f = b as Partial<FeedbackBody>;
  if (typeof f.playerId !== 'string' || !PLAYER_ID.test(f.playerId)) return 'bad player id';
  if (typeof f.text !== 'string' || f.text.trim().length === 0) return 'say something first';
  if (f.text.length > FEEDBACK_MAX) return 'too long';
  if (!short(f.browser, 60) || (f.device !== 'phone' && f.device !== 'computer') || (f.place !== 'poster' && f.place !== 'paused')) return 'bad request';
  return null;
}

export function checkCrash(b: unknown): string | null {
  if (!b || typeof b !== 'object') return 'bad request';
  const c = b as Partial<CrashBody>;
  if (!CRASH_KINDS.includes(c.kind as CrashKind)) return 'bad kind';
  if (!short(c.message, 300) || !short(c.stack, 1500) || !short(c.browser, 60) || !short(c.mode, 20) || !short(c.build, 40)) return 'bad request';
  if (c.device !== 'phone' && c.device !== 'computer') return 'bad request';
  return null;
}

/** What the stats page shows of the beta. */
export interface BetaReport {
  /** Newest first. */
  feedback: { at: number; device: string; browser: string; place: string; text: string }[];
  /** The period's crashes and errors, grouped by what went wrong, most frequent first. */
  crashes: { kind: CrashKind; message: string; count: number; last: number; browsers: string; build: string; stack: string }[];
  /** How many went wrong each day (in Rome), by kind, over the whole report (pages from before 14 Oct 2026 leave it out). */
  crashDays?: { date: string; kind: CrashKind; n: number }[];
  /** Today's and yesterday's main boards, top of each: a lap can be taken off from there. */
  boards: { date: string; trackHash: string; physicsHash: string; total: number; rows: { rank: number; name: string; lapMs: number; ref: string }[] }[];
}

/** "iPhone · Safari 18", "Android · Chrome 140", "Mac · Firefox 131": the kind of device and browser, nothing finer. */
export function browserLabel(ua: string): string {
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && /Mobile\//.test(ua))
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /CrOS/.test(ua)
              ? 'ChromeOS'
              : /Linux/.test(ua)
                ? 'Linux'
                : 'other';
  const major = (re: RegExp): string | null => re.exec(ua)?.[1] ?? null;
  let browser = 'other';
  let v: string | null = null;
  if ((v = major(/(?:Edg|EdgiOS|EdgA)\/(\d+)/))) browser = 'Edge';
  else if ((v = major(/(?:OPR|OPiOS)\/(\d+)/))) browser = 'Opera';
  else if ((v = major(/SamsungBrowser\/(\d+)/))) browser = 'Samsung';
  else if ((v = major(/(?:Firefox|FxiOS)\/(\d+)/))) browser = 'Firefox';
  else if ((v = major(/(?:CriOS|Chrome)\/(\d+)/))) browser = 'Chrome';
  else if ((v = major(/Version\/(\d+).*Safari/))) browser = 'Safari';
  else if (/AppleWebKit/.test(ua) && /Mobile/.test(ua)) browser = 'in-app';
  return `${os} · ${browser}${v ? ` ${v}` : ''}`;
}
